import { Box2, Vector2 } from "three";
import { context } from "tone";
import { HSLColor } from "../colors/HSLColor";
import { HTMLColor } from "../colors/HTMLColor";
import { Property } from "../events/Property";
import { MathX } from "../math/MathX";

export class CanvasDrawer
{
  _canvas:HTMLCanvasElement; 
  get canvas()
  {
    return this._canvas;
  }

  _context:CanvasRenderingContext2D;
  get context()
  {
    return this._context;
  }


  constructor( canvas:HTMLCanvasElement )
  {
    this._canvas = canvas;
    this._context = this._canvas.getContext( "2d" );
  }

  get width()
  {
    return this._canvas.width;
  }

  get height()
  {
    return this._canvas.height;
  }

  clear()
  {
    this._context.clearRect( 0, 0, this.width, this.height );
  }

  clearBox( b: Box2 )
  {
    this._context.clearRect( b.min.x, b.min.y, b.max.x - b.min.x, b.max.y - b.min.y );
  }

  fillBackground( color:HTMLColor )
  {
    this._context.fillStyle = color + "";
    this._context.fillRect( 0, 0, this.width, this.height );
  }

  rangeTransform( start:Vector2, end:Vector2 )
  {
    this._context.translate( -start.x, -start.y );
    this._context.scale( end.x - start.x , end.y - start.y );
  }

  push()
  {
    this._context.save();
  }

  pop()
  {
    this._context.restore();
  }

  map( v:Vector2, min:Vector2, max:Vector2 )
  {
    var x = MathX.map( v.x, min.x, max.x, 0, this.width );
    var y = MathX.map( v.y, min.y, max.y, 0, this.height );

    v.set( x, y );
  }

  setFillStyle( color:HTMLColor )
  {
    this._context.fillStyle = color + "";
  }

  setLineStyle( color:HTMLColor, 
                lineWidth:number=undefined, 
                lineCap:CanvasLineCap=undefined, 
                lineJoin:CanvasLineJoin=undefined  
              )
  {
    this._context.strokeStyle = color + "";

    if ( lineWidth !== undefined )
    {
      this._context.lineWidth = lineWidth;
    }

    if ( lineCap !== undefined )
    {
      this._context.lineCap = lineCap;
    }

    if ( lineJoin !== undefined )
    {
      this._context.lineJoin = lineJoin;
    }
    
  }

  drawLine( start:Vector2, end:Vector2 )
  {
    this._context.beginPath();
    this._context.moveTo( start.x, start.y );
    this._context.lineTo( end.x, end.y );
    this._context.stroke();
  }

  drawLineBox( start:Vector2, end:Vector2 )
  {
    this._context.beginPath();
    this._context.moveTo( start.x, start.y );
    this._context.lineTo( end.x, start.y );
    this._context.lineTo( end.x, end.y );
    this._context.lineTo( start.x, end.y );
    this._context.closePath();

    this._context.stroke();
  }

  drawBezier( a:Vector2, b:Vector2, c:Vector2, d:Vector2 )
  {
    this._context.beginPath();
    this._context.moveTo( a.x, a.y );
    this._context.bezierCurveTo( b.x, b.y, c.x, c.y, d.x, d.y );
    this._context.stroke();
  }

  _ensureNoLinesOverflowStroke( i:number )
  {
    if ( i !== 0 && ( i % 256 === 0 ) )
    {          
      this._context.stroke();
      this._context.beginPath();
    }
    
  }

  _ensureNoLinesOverflowFill( i:number )
  {
    if ( i !== 0 && ( i % 256 === 0 ) )
    {          
      this._context.fill();
      this._context.beginPath();
    }
    
  }

  drawVerticalLines( offset:number, length:number, distance:number, minY:number, maxY:number )
  {
    this._context.beginPath();

    for ( let i = 0; i < length; i++ )
    {
      this._ensureNoLinesOverflowStroke( i );

      let x = i * distance + offset;
      this._context.moveTo( x, minY );
      this._context.lineTo( x, maxY );
    }

    this._context.stroke();
  }

  drawHorizontalLines( offset:number, length:number, distance:number, minX:number, maxX:number )
  {
    this._context.beginPath();

    for ( let i = 0; i < length; i++ )
    {
      this._ensureNoLinesOverflowStroke( i );

      let y = i * distance + offset;
      this._context.moveTo( minX, y );
      this._context.lineTo( maxX, y);
    }

    this._context.stroke();
  }

  drawLines( list:Vector2[] )
  {
    this._context.beginPath();

    for( let i = 0; i < list.length; i+=2 )
    {
      this._ensureNoLinesOverflowStroke( i );

      let start = list[ i ];
      let end   = list[ i + 1 ];

      this._context.moveTo( start.x, start.y );
      this._context.lineTo( end.x, end.y );
    }

    this._context.stroke();
  }

  drawPlot( list:Vector2[] )
  {
    if ( ! list || list.length == 0 )
    {
      return;
    }

    this._context.beginPath();

    this._context.moveTo( list[ 0 ].x, list[ 0 ].y );

    for( let i = 1; i < list.length; i++ )
    {          
      this._context.lineTo( list[ i ].x, list[ i ].y );
    }

    this._context.stroke();
  }

  drawPlotFromValues( list:number[], normalizeIndex:boolean = true, min:Vector2 = null, max:Vector2 = null )
  {
    var points = list.map(
      ( v, i )=>
      {
        var p = new Vector2( i, v );

        if ( normalizeIndex )
        {
          p.x = p.x / list.length;
        }
         
        if ( min && max )
        {
          this.map( p, min, max );
        }

        return p;

      }
    ) 

    this.drawPlot( points );
  }

  drawCircles( positions:Vector2[], size:number, offset:Vector2 )
  {
    let radius = size / 2;
    
    
    for ( let i = 0; i < positions.length; i++ )
    {
      let p = positions[ i ];
      this._context.beginPath();
      this._context.arc( p.x + offset.x, p.y + offset.y, radius, 0, 360 );
      this._context.fill();
    }

    
  }

  drawWaveShapeCenterStroke( minMaxData:Vector2[], offset:Vector2 )
  {
    this._context.beginPath();

    let firstMin = minMaxData[ 0 ].x;
    let firstmax = minMaxData[ 0 ].y;

    let firstCenterX = ( firstMin + firstmax ) * 0.5;

    let firstX = offset.x;

    this._context.moveTo( firstX, firstCenterX + offset.y );

    for( let i = 1; i < minMaxData.length; i++ )
    {
      let min = minMaxData[ i ].x;
      let max = minMaxData[ i ].y;

      let centerY = ( min + max ) * 0.5;

      let x = i + offset.x;

      this._context.lineTo( x, centerY + offset.y );
    }

    this._context.stroke();
  }

  drawWaveShape( minMaxData:Vector2[], offset:Vector2, minimumHeight:number = 1 )
  {
    let absHalfHeight = Math.abs( minimumHeight / 2 );

    this._context.beginPath();

    let output = new Vector2( 0, 0 );

    for( let i = 0; i < minMaxData.length; i++ )
    {
      this._ensureNoLinesOverflowStroke( i );

      this._getMinMax( minMaxData, i, output );

      let min = output.x
      let max = output.y;

      let centerY = ( min + max ) * 0.5;

      min = Math.min( min, centerY - absHalfHeight );
      max = Math.max( max, centerY + absHalfHeight );

      let x = i + offset.x;

      this._context.moveTo( x, min + offset.y );
      this._context.lineTo( x, max + offset.y );
    }

    this._context.stroke();
  }

  drawGradientWaveShape( colorEdge:HTMLColor, colorCenter:HTMLColor, edgeFadePosition:number, minMaxData:Vector2[], offset:Vector2, minimumHeight:number = 3 )
  {
    let absHalfHeight = Math.abs( minimumHeight / 2 );


    let output = new Vector2( 0, 0 );
    let edgeColorString = colorEdge + "";
    let centerColorString = colorCenter + "";

    for( let i = 0; i < minMaxData.length; i++ )
    {
      if ( isNaN( minMaxData[ i ].x ) )
      {
        continue;
      }

      this._getMinMax( minMaxData, i, output );

      let min = output.x;
      let max = output.y;

      
      let centerY = ( min + max ) * 0.5;

      min = Math.min( min, centerY - absHalfHeight );
      max = Math.max( max, centerY + absHalfHeight );

      
      let x = i + offset.x;

      if ( min === centerY )
      {
        min --;
      }

      if ( max === centerY )
      {
        max ++;
      }
       
      let top    = this._context.createLinearGradient( x, min + offset.y, x, centerY + offset.y );
      let bottom = this._context.createLinearGradient( x, centerY + offset.y , x, max + offset.y );     

      top.addColorStop( 0, edgeColorString );
      top.addColorStop( edgeFadePosition, centerColorString );

      bottom.addColorStop( 1 - edgeFadePosition, centerColorString );
      bottom.addColorStop( 1, edgeColorString );

      this._context.strokeStyle = top;
      this._context.beginPath();
      this._context.moveTo( x, min + offset.y );
      this._context.lineTo( x, centerY + offset.y );
      this._context.stroke();

      this._context.strokeStyle = bottom;
      this._context.beginPath();
      this._context.moveTo( x, centerY + offset.y );
      this._context.lineTo( x, max + offset.y );
      this._context.stroke();
    }

    
  }

  _getMinMax( minMaxData:Vector2[], index:number, output:Vector2 )
  {
    let left   = minMaxData[ Math.max( 0, index - 1 ) ];
    let center = minMaxData[ index ];
    let right  = minMaxData[ Math.min( minMaxData.length - 1, index + 1 ) ];

    output.x = center.x;
    output.y = center.y;

    if ( center.x > left.y || center.x > right.y )
    {
      if ( left.y < right.y )
      {
        output.x = ( center.x + left.y ) * 0.5;
      }
      else
      {
        output.x = ( center.x + right.y ) * 0.5;
      }
    }

    if ( center.y < left.x || center.y < right.x )
    {
      if ( left.x > right.x )
      {
        output.y = ( center.y + left.x ) * 0.5;
      }
      else
      {
        output.y = ( center.y + right.x ) * 0.5;
      }
    }

  }



}